import React, { useRef, useEffect, useState, forwardRef } from 'react';
import styled from 'styled-components';
import { minBp, maxBp } from 'src/lib/theme';

export const Image = styled.img`
  ${({ zIndex, isFullScreen, lensImage }) => `
    width: calc(100% - 20px);
    object-fit: contain;
    ${zIndex ? `z-index: ${zIndex}` : ''};

    @media ${minBp('tablet')} {
      width: 100%;
    }

    // For iPad Pro portrait
    @media ${minBp('tabletLarge')} and (orientation: portrait) {
      width: 85%;
    }

    @media ${maxBp('tabletLarge')} and (orientation: landscape) { 
      max-width: 500px;
    }

    // Allow room for buttons on iPad landscape
    @media ${minBp('tabletLarge')} and ${maxBp('desktop')} and (orientation: landscape) {
      width: 750px;
    }

    // All other bigger mobiles (iPhone, Pixel, Samsung)
    @media (max-width: 813px) and (orientation: landscape) { 
      max-width: 450px;
    }

    // iPhone 5/5s
    @media (max-width: 568px) and (orientation: landscape) { 
      max-width: 375px;
    }
    
    &:not(:first-of-type) {
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);

      @media ${minBp('tablet')} { 
        left: 0;
        transform: none;
      }

      @media ${minBp('tabletLarge')} and (orientation: portrait) { 
        left: 50%;
        transform: translateX(-50%);
      }

      @media (min-width: 1250px) { 
        left: 50%;
        transform: translateX(-50%);
      }

      @media ${minBp('desktop')} { 
        left: 0;
        transform: none;
        height: 100%;
      }
    }

    // IE hacks for bike image positioning when full screen
    @media screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none) {
      top: ${isFullScreen && !lensImage ? '50% !important' : 'auto'};
      height: ${isFullScreen && !lensImage ? '100% !important' : 'auto'};
      max-height: ${isFullScreen && !lensImage ? '90% !important' : 'auto'};

      ${
        isFullScreen && !lensImage
          ? `      
        width: auto !important;
        transform: translate(-50%, -50%) !important;
        left: 50% !important;
      `
          : ``
      }
    }
  `}
`;

const Images = styled.div`
  position: absolute;

  > img {
    max-width: none;
  }

  ${Image} {
    // IE hacks for bike image positioning when full screen
    @media screen and (-ms-high-contrast: active), screen and (-ms-high-contrast: none) {
      top: auto !important;
      height: auto !important;
      max-height: 100% !important;
    }
  }
`;

const ClickCatch = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 5;
`;

const Outer = styled.div``;

const DivIgnoreProps = forwardRef(({ on, ...props }, ref) => <div ref={ref} {...props} />);

const Wrap = styled(DivIgnoreProps)`
  display: block;
  height: 100%;
  text-align: center;

  @media ${minBp('desktop')} {
    display: flex;
    justify-content: center;
  }

  ${({ on }) =>
    on
      ? `  
    z-index: 6;
    position: relative;
  `
      : ``}
`;

const Lens = styled(DivIgnoreProps)`
  background: white;
  border-radius: 100%;
  opacity: 0;
  transition: opacity 0.3s;
  width: 100px;
  height: 100px;
  position: absolute;
  overflow: hidden;
  border: 6px solid #fff;
  z-index: 6;
  display: none;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.1);
  z-index: 98;

  ${({ on }) =>
    on
      ? `
    opacity: 1;
  `
      : ``}

  @media ${minBp('tablet')} {
    width: 200px;
    height: 200px;
    pointer-events: none;
  }

  @media ${minBp('desktop')} {
    width: 300px;
    height: 300px;
  }
`;

const Magnify = ({ children, on = false, onOff = () => {}, triggerRef, className, isFullScreen, outerWrapRef }) => {
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);

  const wrapRef = useRef();
  const lensRef = useRef();
  const imagesRef = useRef();

  const setLensPosition = (cursorX, cursorY) => {
    const lensEl = lensRef.current;

    if (!lensEl) {
      return;
    }

    const lensWidthHalf = lensEl.offsetWidth / 2;
    const lensHeightHalf = lensEl.offsetHeight / 2;

    lensEl.style.left = `${cursorX - lensWidthHalf}px`;
    lensEl.style.top = `${cursorY - lensHeightHalf}px`;
  };

  useEffect(() => {
    const wrapEl = wrapRef.current;
    const lensEl = lensRef.current;
    const imagesEl = imagesRef.current;
    const outerWrapEl = outerWrapRef.current;

    const setZoomedImagePosition = (cursorX, cursorY) => {
      const lensWidthHalf = lensEl.offsetWidth / 2;
      const lensHeightHalf = lensEl.offsetHeight / 2;

      imagesEl.style.left = `${-((cursorX / wrapEl.offsetWidth) * imagesEl.offsetWidth - lensWidthHalf)}px`;
      imagesEl.style.top = `${-((cursorY / wrapEl.offsetHeight) * imagesEl.offsetHeight - lensHeightHalf)}px`;
    };

    const handleMouseMove = event => {
      const { top: wrapTop, left: wrapLeft } = wrapEl.getBoundingClientRect();
      const x = event.pageX - wrapLeft;
      const y = event.pageY - wrapTop;

      const {
        right: imagePaneRight,
        bottom: imagePaneBottom,
        left: imagePaneLeft,
      } = outerWrapEl.parentElement.getBoundingClientRect();

      const cursorOutsideImage =
        event.pageY < wrapTop ||
        event.pageX > imagePaneRight ||
        event.pageY > imagePaneBottom ||
        event.pageX < imagePaneLeft;

      // Turn off if we go out of bounds
      if (cursorOutsideImage && !document.querySelector('.js-fullscreen')) {
        onOff();
      }

      setLensPosition(x, y);
      setZoomedImagePosition(x, y);
    };

    const init = () => {
      lensEl.style.display = 'block';
      Array.from(imagesEl.children).forEach((imageEl, i) => {
        if (i === 0) {
          imageEl.style.maxWidth = 'none';
          imageEl.style.width = 'auto';
          imageEl.style.height = 'auto';
          imageEl.style.objectFit = 'none';
        }
      });

      if (startX && startY) {
        handleMouseMove({
          pageX: startX,
          pageY: startY,
        });

        setZoomedImagePosition(startX, startY);
      }

      window.addEventListener('click', onOff);
    };

    if (!wrapEl || !lensEl) {
      return;
    }

    init();

    window.addEventListener('mouseenter', handleMouseMove);
    window.addEventListener('mousemove', handleMouseMove);

    return () => [
      window.removeEventListener('mousemove', handleMouseMove),
      window.removeEventListener('mouseenter', handleMouseMove),
      window.removeEventListener('click', onOff),
    ];
  }, [isFullScreen, on, onOff, outerWrapRef, startX, startY]);

  useEffect(() => {
    const triggerEl = triggerRef.current;

    if (triggerEl) {
      const setInitialPosition = () => {
        const triggerElRect = triggerEl.getBoundingClientRect();

        setStartX(triggerElRect.left + triggerEl.offsetWidth / 2);
        setStartY(triggerElRect.top + triggerEl.offsetHeight / 2);

        triggerEl.removeEventListener('click', setInitialPosition);
      };

      triggerEl.addEventListener('click', setInitialPosition);
    }
  }, [triggerRef]);

  return (
    <Outer className={className}>
      {on ? <ClickCatch /> : null}
      <Wrap on={on} ref={wrapRef}>
        {children}
      </Wrap>
      {on ? (
        <Lens ref={lensRef} on={on} data-testid="lens">
          <Images ref={imagesRef}>{children}</Images>
        </Lens>
      ) : null}
    </Outer>
  );
};

export default Magnify;
